// Client-side helpers for the cadence editor. The SERVER's
// server/lib/cadenceValidation.js is authoritative for validation — the
// editor shows whatever the last save/fetch returned; these helpers only
// shape data for display and edits.

import { formatTime } from './TimeField';

// Definitions stopped carrying scripts on 2026-08-11 — script text lives in
// the LIVE store (cadence_settings) and the ScriptsPanel edits it directly.
export const cloneDefinition = (definition) =>
  JSON.parse(JSON.stringify(definition || { steps: [] }));

// stepNumber is ALWAYS derived from array order — the editor reorders the
// array and renumbers, never edits numbers directly.
export const renumberSteps = (steps) =>
  steps.map((step, index) => ({ ...step, stepNumber: index + 1 }));

// Chains always follow THE PREVIOUS step of their day (Nic 2026-08-09: the
// checklist is linear — do this, then this). chainAfter stays explicit in
// the data, but the editor derives it: after any structural change, every
// chained step re-wires to whatever now sits directly above it in its day.
// A chained step that ends up FIRST in its day falls back to a plain clock
// window (there is nothing to follow).
export const normalizeChains = (steps) => {
  steps.forEach((step, i) => {
    if (step.anchor !== 'chain') return;
    let prev = null;
    for (let j = 0; j < i; j += 1) {
      if (steps[j].day === step.day) prev = steps[j];
    }
    if (prev) {
      step.chainAfter = prev.stepKey;
    } else {
      step.anchor = 'day';
      delete step.chainAfter;
      delete step.chainOffsetStartMinutes;
      delete step.chainOffsetEndMinutes;
      step.windowStartLocal = step.windowStartLocal || '08:00';
      step.windowEndLocal = step.windowEndLocal || '10:00';
    }
  });
  return steps;
};

export const stepWindowSummary = (step, steps = []) => {
  if (step.anchor === 'arrival') {
    return `${step.offsetStartMinutes ?? '?'}-${step.offsetEndMinutes ?? '?'} min after arrival`;
  }
  if (step.anchor === 'day') {
    return `${formatTime(step.windowStartLocal)} - ${formatTime(step.windowEndLocal)}`;
  }
  if (step.anchor === 'chain') {
    const dep = steps.find((s) => s.stepKey === step.chainAfter);
    return `${step.chainOffsetStartMinutes ?? '?'}-${step.chainOffsetEndMinutes ?? '?'} min after ${dep?.label || step.chainAfter || '?'}`;
  }
  return 'No window';
};

// A fresh step lands day-anchored with a sane window; the key is derived
// and de-duplicated so validation never trips on a brand-new row.
export const newStep = (day, existingSteps) => {
  const existing = new Set(existingSteps.map((s) => s.stepKey));
  const base = `d${day}_call`;
  let n = 1;
  while (existing.has(`${base}_${n}`)) n += 1;
  return {
    stepKey: `${base}_${n}`,
    stepNumber: 0, // renumbered on insert
    label: 'New call',
    day,
    type: 'call',
    anchor: 'day',
    windowStartLocal: '08:00',
    windowEndLocal: '10:00',
    leaveVoicemail: false,
    morningLeadOnly: false,
    scriptKey: null,
  };
};

// Steps grouped by day, days sorted — the editor's main structure.
export const groupStepsByDay = (steps) => {
  const byDay = new Map();
  steps.forEach((step) => {
    const day = Number.isInteger(step.day) ? step.day : 0;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(step);
  });
  return [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([day, daySteps]) => ({ day, steps: daySteps }));
};

const stepFingerprint = (step) => JSON.stringify(step);

// Publish-confirm diff: counts only (added/removed/changed), keyed by
// stepKey. Scripts don't diff here — they live OFF the versions
// (2026-08-11) and change independently of any publish.
export const diffDefinitions = (from, to) => {
  const fromSteps = new Map((from?.steps || []).map((s) => [s.stepKey, s]));
  const toSteps = new Map((to?.steps || []).map((s) => [s.stepKey, s]));
  const stepsAdded = [...toSteps.keys()].filter((k) => !fromSteps.has(k));
  const stepsRemoved = [...fromSteps.keys()].filter((k) => !toSteps.has(k));
  const stepsChanged = [...toSteps.keys()].filter(
    (k) =>
      fromSteps.has(k) &&
      stepFingerprint(fromSteps.get(k)) !== stepFingerprint(toSteps.get(k)),
  );

  const hoursChanged =
    from?.businessStartLocal !== to?.businessStartLocal ||
    from?.businessEndLocal !== to?.businessEndLocal ||
    Boolean(from?.skipWeekends) !== Boolean(to?.skipWeekends);

  return {
    stepsAdded,
    stepsRemoved,
    stepsChanged,
    hoursChanged,
  };
};
